/**
 * OutputDefaultsFields — the five output-defaults fields and the marketplace-preset row,
 * written ONCE (WS-B B-10 / AUD-6 MIN-11).
 *
 * Settings → Output defaults and Admin → Workspace defaults are the same form: Width,
 * Height, Format, Quality, Max canvas (px), then "Add marketplace preset". They were two
 * independent copies, each with its own private `FORMATS` list, so the two surfaces could
 * silently disagree about which formats exist, what a field is called or what order they come
 * in — and the customer-facing one is the copy a customer reads before every export.
 *
 * What is deliberately NOT shared, because the two surfaces genuinely differ:
 *   - the SAVE path (a local device override vs a platform-wide PATCH);
 *   - the per-field `OutputMeta` source/reset affordance, which only makes sense where there
 *     is something to inherit FROM — passed in through `meta`;
 *   - the surrounding copy ("this device only" vs "every tenant inherits these").
 * Editor → Settings is literally the same `SettingsPanel` component, so it is fixed by
 * construction rather than by a third call site.
 */
import type { ReactNode } from "react";
import { MARKETPLACE_PRESETS, type OutputPreset } from "./output.js";

/** The output formats the app can write. ONE list — both pages declared their own. */
export const OUTPUT_FORMATS = ["png", "jpg", "webp"] as const;

/** The five values the form edits, plus the preset list the row appends to. */
export interface OutputDefaultsValues {
  output_width: number;
  output_height: number;
  output_format: string;
  output_quality: number;
  max_canvas_px: number | null;
  output_presets: OutputPreset[];
}

export interface OutputDefaultsFieldsProps {
  values: OutputDefaultsValues;
  /** Called with the wire key and its new value. The caller owns what "save" means. */
  onChange: (key: keyof OutputDefaultsValues, value: unknown) => void;
  /** `data-testid` prefix — `out` on the customer Settings page, `wd` on the admin tab. Both
   *  are the contract their own tests and save paths key on, so neither can move. */
  testIdPrefix: string;
  /** Per-field source/reset affordance. Rendered under a field when provided; the admin tab
   *  passes nothing, because a platform default has nothing above it to inherit from. */
  meta?: (key: keyof OutputDefaultsValues) => ReactNode;
  /** Per-field explanation, rendered INSIDE the field's label, right after its text (rule 10:
   *  an `InfoHint` sits with the thing it explains). It is a separate slot from `meta` for a
   *  reason: `meta` is a row BELOW the control, and a ⓘ down there is a glyph with nothing
   *  beside it (fix round 1, F9). The admin tab passes nothing. */
  labelHint?: (key: keyof OutputDefaultsValues) => ReactNode;
  /** Render Max canvas as BLANK rather than its resolved number (the customer page does this
   *  when the value is the inherited default, so "empty" reads as "inherit the ceiling"). */
  maxCanvasBlank?: boolean;
  /** Wrap each field in an extra `div.field[data-testid="setting-<key>"]`, which the customer
   *  Settings page's own tests target. */
  fieldTestIds?: boolean;
}

export function OutputDefaultsFields({
  values, onChange, testIdPrefix, meta, labelHint, maxCanvasBlank, fieldTestIds,
}: OutputDefaultsFieldsProps) {
  /** One field: the label + control, plus the optional meta line, in the wrapper each page
   *  expects. Keeping the two shapes here is what lets both pages' existing tests pass. */
  const field = (key: keyof OutputDefaultsValues, label: string, control: ReactNode) => {
    const hint = labelHint?.(key);
    // The ⓘ goes in the label's own `<span>`, after a real space, so it reads as part of
    // "Quality ⓘ" rather than sitting alone on the row under the control.
    const labelText = <span>{label}{hint ? <>{" "}{hint}</> : null}</span>;
    const inner = (
      <>
        <label>{labelText}{control}</label>
        {meta?.(key)}
      </>
    );
    if (fieldTestIds) {
      return <div className="field" data-testid={`setting-${key}`} key={key}>{inner}</div>;
    }
    return <label className="field" key={key}>{labelText}{control}</label>;
  };

  return (
    <>
      <div className="settings__grid" data-testid={`${testIdPrefix}-grid`}>
        {field("output_width", "Width", (
          <input type="number" data-testid={`${testIdPrefix}-width`} value={values.output_width}
            onChange={(e) => onChange("output_width", Number(e.target.value))} />
        ))}
        {field("output_height", "Height", (
          <input type="number" data-testid={`${testIdPrefix}-height`} value={values.output_height}
            onChange={(e) => onChange("output_height", Number(e.target.value))} />
        ))}
        {field("output_format", "Format", (
          <select data-testid={`${testIdPrefix}-format`} value={values.output_format}
            onChange={(e) => onChange("output_format", e.target.value)}>
            {OUTPUT_FORMATS.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        ))}
        {field("output_quality", "Quality", (
          <input type="number" data-testid={`${testIdPrefix}-quality`} min={5} max={100}
            value={values.output_quality}
            onChange={(e) => onChange("output_quality", Number(e.target.value))} />
        ))}
        {field("max_canvas_px", "Max canvas (px)", (
          <input type="number" data-testid={`${testIdPrefix}-max-canvas`} min={1}
            value={maxCanvasBlank ? "" : values.max_canvas_px ?? ""}
            placeholder="default ceiling"
            onChange={(e) => onChange("max_canvas_px", e.target.value === "" ? null : Number(e.target.value))} />
        ))}
      </div>
    </>
  );
}

/** The marketplace-preset buttons — the same row on both pages. */
export function OutputPresetButtons({
  presets, onAdd,
}: {
  presets: OutputPreset[];
  onAdd: (next: OutputPreset[]) => void;
}) {
  return (
    <>
      <span>Add marketplace preset:</span>
      {MARKETPLACE_PRESETS.map((p) => (
        <button type="button" key={p.name} className="btn btn--sm btn--ghost"
          onClick={() => onAdd([...presets, p])}>
          {p.name}
        </button>
      ))}
    </>
  );
}
